import { Typography, Select, FormControl, InputLabel, type FormControlProps } from '@tunasong/ui-lib'
import { logger } from '@tunasong/models'
import type { ChangeEvent, FC } from 'react'
import { useEffect, useState } from 'react'
import { usePermission } from './audio-permissions.hook.js'
import { getDevices } from './devices.js'

export interface DeviceSelectProps extends Omit<FormControlProps, 'onChange'> {
  className?: string
  type: MediaDeviceKind
  selectedId?: string | null
  onChange(selected: MediaDeviceInfo): void
}

const labels: Record<MediaDeviceKind, string> = {
  audioinput: 'Audio Input',
  audiooutput: 'Audio Output',
  videoinput: 'Video Input',
}

export const DeviceSelect: FC<DeviceSelectProps> = props => {
  const { onChange, selectedId = 'default', type, ...formControlProps } = props

  const [devices, setDevices] = useState<MediaDeviceInfo[] | null>(null)
  const { permission } = usePermission('microphone')

  useEffect(() => {
    // Subscribe to device changes
    const handleDeviceChange = () => {
      void getDevices(type).then(devices => {
        logger.debug(`${type} devices available`, devices)
        setDevices(devices)
      })
    }
    // Initial load when microphone permission is granted
    if (permission === 'granted' || type === 'audiooutput') {
      handleDeviceChange()
    }
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange)
    }
  }, [type, permission])

  const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const val = event.target.value
    const d = devices?.find(item => item.deviceId === val)
    if (d) {
      onChange(d)
    }
  }

  const label = labels[type]

  const validDevices = devices?.filter(d => d.deviceId)

  if (!validDevices || validDevices.length === 0) {
    return <Typography variant="caption">No {label} devices available</Typography>
  }

  return (
    <FormControl fullWidth={true} {...formControlProps} style={{ marginTop: 8, ...formControlProps.style }}>
      <InputLabel htmlFor={`${type}-select`}>{label}</InputLabel>
      <Select
        fullWidth={true}
        label={label}
        id={`${type}-select`}
        value={selectedId ?? ''}
        onChange={handleChange}
      >
        {validDevices.map(device => (
          <option key={device.deviceId} value={device.deviceId}>
            {device.deviceId === 'default' ? 'Default' : device.label}
          </option>
        ))}
      </Select>
    </FormControl>
  )
}

export default DeviceSelect
